import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, ChevronRight, Lock, LayoutDashboard, User, Camera, ImagePlus, Trash2, BadgeCheck, LifeBuoy, HelpCircle, FileText, Shield, Moon, Sun, Users, MapPin, FileDown, Flag, Scale, IdCard } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Spinner, AuthedAvatar, BlueCheck } from "../components/common";
import { CropModal } from "../components/CropModal";

function abbreviateName(full) {
  if (!full) return "";
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

function SettingRow({ icon: Icon, label, desc, onClick, trailing, danger, noChevron }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      {...(onClick ? { onClick } : {})}
      className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${onClick ? "hover:bg-secondary/50 active:bg-secondary" : ""}`}
    >
      <div className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${danger ? "bg-destructive/10 text-destructive" : "bg-secondary text-primary"}`}>
        <Icon size={18} />
      </div>
      <span className="flex-1 min-w-0">
        <span className={`block font-medium leading-tight ${danger ? "text-destructive" : ""}`}>{label}</span>
        {desc && <span className="block text-[11px] text-muted-foreground font-normal mt-0.5">{desc}</span>}
      </span>
      <span className="flex items-center gap-2 shrink-0">
        {trailing}
        {onClick && !noChevron && <ChevronRight size={18} className="text-muted-foreground" />}
      </span>
    </Comp>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="mb-5">
      <div className="px-1 mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
        {desc && <div className="text-[11px] text-muted-foreground/70 mt-0.5">{desc}</div>}
      </div>
      <div className="rounded-3xl bg-card border border-border divide-y divide-border overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function ProfileCompleteness({ user, verified, nav, onPhoto, onAddress }) {
  const items = [
    { ok: !!user.profile_photo, label: "Add a photo", go: onPhoto },
    { ok: !!user.phone, label: "Add phone number", go: () => nav("/app/personal") },
    { ok: !!(user.address && user.address.line1), label: "Add home address", go: onAddress },
    { ok: !!user.email_verified, label: "Verify email", go: () => nav("/app/personal") },
    { ok: verified, label: "Verify identity", go: () => nav("/app/kyc") },
  ];
  const done = items.filter((i) => i.ok).length;
  const pct = Math.round((done / items.length) * 100);
  if (done === items.length) return null;
  const missing = items.filter((i) => !i.ok);

  return (
    <div className="rounded-3xl bg-card border border-border p-5 mb-4" data-testid="profile-completeness">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-display font-semibold">Complete your profile</h2>
        <span data-testid="profile-completeness-value" className="text-sm font-bold text-primary">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-4">
        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-2">
        {missing.map((m, i) => (
          <button key={i} onClick={m.go} data-testid={`complete-${i}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-secondary text-foreground/80 transition-transform active:scale-95">
            <ChevronRight size={13} className="text-primary" /> {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const [menu, setMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setMenu(false);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const uploadCropped = async (blob) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", blob, "avatar.jpg");
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data); setCropSrc(null); toast.success("Photo updated");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    setMenu(false); setUploading(true);
    try { const { data } = await api.delete("/profile/photo"); setUser(data); toast.success("Photo removed"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };

  const verified = user.verification_status === "verified";
  const joinedYear = user.created_at ? new Date(user.created_at).getFullYear() : "";
  const active = (user.account_status || "").toLowerCase() === "active";

  return (
    <div className="animate-fade-up pb-4">
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-[28px] p-6 mb-5 text-white shadow-lg shadow-primary/20"
        style={{ background: "linear-gradient(140deg,#6B3BF5 0%,#4B23CC 100%)" }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full bg-black/10 blur-2xl" />
        <div className="relative flex flex-col items-center text-center">
          <div className="relative">
            <button data-testid="photo-menu-btn" onClick={() => setMenu((v) => !v)} disabled={uploading}
              className="relative rounded-full transition-transform active:scale-95 disabled:opacity-70">
              <AuthedAvatar src={user.profile_photo} name={user.display_name} size={96}
                className="w-24 h-24 rounded-full ring-4 ring-white/25 text-4xl font-display" />
              {uploading && <span className="absolute inset-0 grid place-items-center rounded-full bg-black/40"><Spinner className="w-5 h-5 text-white" /></span>}
            </button>
            {menu && (
              <div data-testid="photo-menu" className="absolute z-20 top-full mt-2 left-1/2 -translate-x-1/2 w-44 rounded-2xl bg-card text-foreground border border-border shadow-xl overflow-hidden">
                <button data-testid="photo-choose" onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-secondary"><ImagePlus size={16} className="text-primary" /> Choose photo</button>
                <button data-testid="photo-camera" onClick={() => camRef.current?.click()} className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-secondary border-t border-border"><Camera size={16} className="text-primary" /> Take photo</button>
                {user.profile_photo && <button data-testid="photo-remove" onClick={removePhoto} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-destructive hover:bg-secondary border-t border-border"><Trash2 size={16} /> Remove</button>}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pickFile} className="hidden" data-testid="photo-file-input" />
            <input ref={camRef} type="file" accept="image/*" capture="user" onChange={pickFile} className="hidden" data-testid="photo-camera-input" />
          </div>
          <h1 className="font-display text-2xl font-semibold mt-4">{abbreviateName(user.full_name)}</h1>
          <div className="flex items-center justify-center gap-1.5 text-sm font-mono mt-1 text-white/90">
            {user.cash_tag}
            <BlueCheck show={!!user.identity_verified || (user.badge_active && user.badge_color === "blue")} size={18} testid="verified-seal" />
          </div>
          <div className="flex items-center gap-2 mt-4">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm">
              <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-300" : "bg-amber-300"}`} />
              {(user.account_status || "active").toUpperCase()}
            </span>
            {joinedYear && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1 rounded-full bg-white/10">
                Member since {joinedYear}
              </span>
            )}
          </div>
        </div>
      </div>

      <ProfileCompleteness user={user} verified={verified} nav={nav}
        onPhoto={() => setMenu(true)} onAddress={() => nav("/app/address")} />

      {/* VERIFICATION & SECURITY */}
      <Section title="Verification & Security" desc="Your badge, identity and account protection">
        <SettingRow icon={BadgeCheck} label="Jack Cash Plus" desc="Blue badge + 24/7 Account Guard · $4.99/mo"
          onClick={() => nav("/app/badge")}
          trailing={user.plus_active
            ? <span className="text-[11px] font-bold px-2.5 py-1 rounded-full text-[#2F80FF] bg-[#2F80FF]/10 border border-[#2F80FF]/20">PLUS ACTIVE</span>
            : <span className="text-[11px] font-bold text-primary px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">GET PLUS</span>} />
        <SettingRow icon={IdCard} label="Identity verification" desc="KYC identity check"
          onClick={() => nav("/app/kyc")}
          trailing={verified
            ? <span className="text-[11px] font-bold text-success px-2.5 py-1 rounded-full bg-success/10 border border-success/20">VERIFIED</span>
            : user.verification_status === "pending"
              ? <span className="text-[11px] font-bold text-warning px-2.5 py-1 rounded-full bg-warning/10 border border-warning/30">PENDING</span>
              : <span className="text-[11px] font-bold text-primary px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">VERIFY NOW</span>} />
        <SettingRow icon={Lock} label="Security settings" desc="PIN, password, 2FA & devices" onClick={() => nav("/app/security")} />
      </Section>

      {/* PERSONAL INFORMATION */}
      <Section title="Personal Information" desc="Your details, address and statements">
        <SettingRow icon={User} label="Personal information" desc="Name, username and contact details"
          onClick={() => nav("/app/personal")} />
        <SettingRow icon={MapPin} label="Home address" desc="Used for statements & card shipping"
          onClick={() => nav("/app/address")}
          trailing={user.address
            ? <span className="text-[11px] font-semibold text-muted-foreground truncate max-w-[120px]">{user.address.city}, {user.address.state}</span>
            : <span className="text-[11px] font-bold text-primary px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">ADD</span>} />
        <SettingRow icon={FileDown} label="Account statements" desc="Download monthly PDF statements" onClick={() => nav("/app/statements")} />
      </Section>

      {/* FAMILY */}
      {!user.family?.is_teen && (
        <Section title="Family" desc="Managed accounts for your kids">
          <SettingRow icon={Users} label="Teen accounts" desc="Ages 13–17 · you set the rules" onClick={() => nav("/app/family")} />
        </Section>
      )}

      {/* APPEARANCE */}
      <Section title="Appearance" desc="How Jack Cash looks on your device">
        <SettingRow icon={theme === "dark" ? Moon : Sun} label="Dark mode" onClick={toggleTheme} noChevron
          trailing={<span data-testid="dark-mode-toggle" className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${theme === "dark" ? "bg-primary" : "bg-muted"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${theme === "dark" ? "translate-x-6" : "translate-x-1"}`} />
          </span>} />
      </Section>

      {/* SUPPORT */}
      <Section title="Support" desc="We're here to help">
        <SettingRow icon={LifeBuoy} label="Contact support" onClick={() => nav("/app/assistant")} />
        <SettingRow icon={HelpCircle} label="Help center / FAQ" onClick={() => nav("/legal/faq")} />
        <SettingRow icon={Flag} label="Report a problem" onClick={() => nav("/app/assistant")} />
      </Section>

      {/* LEGAL */}
      <Section title="Legal">
        <SettingRow icon={FileText} label="Terms of Service" onClick={() => nav("/legal/terms")} />
        <SettingRow icon={Shield} label="Privacy Policy" onClick={() => nav("/legal/privacy")} />
        <SettingRow icon={Scale} label="Licenses" onClick={() => nav("/legal/licenses")} />
      </Section>

      {/* ADMIN */}
      {user.role === "admin" && (
        <Section title="Admin">
          <div data-testid="go-admin-btn"><SettingRow icon={LayoutDashboard} label="Admin dashboard" desc="Staff controls & analytics" onClick={() => nav("/admin")} /></div>
        </Section>
      )}

      <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login", { replace: true }); }}
        className="w-full h-14 rounded-2xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2 transition-transform duration-200 active:scale-[0.98]">
        <LogOut size={18} /> Log out
      </button>

      {cropSrc && <CropModal imageSrc={cropSrc} onCancel={() => setCropSrc(null)} onDone={uploadCropped} />}
    </div>
  );
}
